import React, { useState } from 'react';
import { API_BASE_URL } from '../utils/api';

const BulkUpload = () => {
    const [file, setFile] = useState(null);
    const [deleteExisting, setDeleteExisting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);
    const [result, setResult] = useState(null);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setMessage(null);
        setResult(null);
    };

    const handleUpload = async () => {
        if (!file) {
            setMessage({ type: 'error', text: 'Please select a file first.' });
            return;
        }

        if (deleteExisting && !window.confirm("WARNING: This will DELETE all existing products and categories. Are you sure?")) {
            return;
        }

        const formData = new FormData();
        formData.append('deleteExisting', deleteExisting);
        formData.append('file', file);

        setUploading(true);
        setMessage(null);
        setResult(null);

        try {
            const response = await fetch(`${API_BASE_URL}/upload/products`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setMessage({ type: 'success', text: data.message });
                setResult(data.results);
            } else {
                setMessage({ type: 'error', text: data.message || 'Upload failed' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Network error or server unavailable.' });
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Bulk Product Upload</h1>

            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <h3 className="font-semibold text-blue-700 mb-2">Instructions:</h3>
                <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                    <li>Upload an Excel file (.xlsx or .xls)</li>
                    <li>Required Columns: <strong>CATEGORY, BRAND, MODEL</strong></li>
                    <li>Optional Columns: SUB_CATEGORY, PORTS, PRINT_WIDTH, SPECIFICATION, WARRANTY, DESCRIPTION</li>
                </ul>
            </div>

            <div className="flex flex-col gap-4 mb-6">
                <div className="flex items-center">
                    <input
                        id="deleteExisting"
                        type="checkbox"
                        checked={deleteExisting}
                        onChange={(e) => setDeleteExisting(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="deleteExisting" className="ml-2 block text-sm text-red-600 font-semibold cursor-pointer">
                        Clear existing products and categories before upload
                    </label>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            file:bg-blue-50 file:text-blue-700
                            hover:file:bg-blue-100
                        "
                    />
                    <button
                        onClick={handleUpload}
                        disabled={uploading || !file}
                        className={`px-6 py-2 rounded-lg font-semibold text-white transition
                            ${uploading || !file ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}
                        `}
                    >
                        {uploading ? 'Uploading...' : 'Upload Excel'}
                    </button>
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-md mb-6 ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {message.text}
                </div>
            )}

            {result && (
                <div className="border-t pt-6">
                    <h2 className="text-lg font-semibold mb-4">Upload Summary</h2>
                    <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                        <div className="bg-gray-100 p-3 rounded">
                            <span className="block text-2xl font-bold">{result.total}</span>
                            <span className="text-gray-600 text-sm">Total Rows</span>
                        </div>
                        <div className="bg-green-100 p-3 rounded text-green-800">
                            <span className="block text-2xl font-bold">{result.success}</span>
                            <span className="text-sm">Success</span>
                        </div>
                        <div className="bg-red-100 p-3 rounded text-red-800">
                            <span className="block text-2xl font-bold">{result.errors.length}</span>
                            <span className="text-sm">Errors</span>
                        </div>
                    </div>

                    {result.errors.length > 0 && (
                        <div className="mt-4">
                            <h3 className="font-semibold text-red-600 mb-2">Error Log:</h3>
                            <div className="bg-gray-50 p-4 rounded border max-h-64 overflow-y-auto text-sm font-mono text-red-500">
                                {result.errors.map((err, idx) => (
                                    <div key={idx} className="mb-1">{err}</div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BulkUpload;
